import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from './ui/sheet';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { useDomains, useUploadAsset, useZones } from '@/lib/queries';
import type { AssetEntityType } from '@/lib/types';
import { Camera, FileText, X } from 'lucide-react';

/**
 * Upload sheet — mobile-forward:
 *   - File input declares `capture="environment"` so iOS/Android
 *     opens the rear camera as the default picker, not a chooser.
 *   - Tag chips track the last-used set in component state so a
 *     dandelion-photo run doesn't re-type "dandelions" three times.
 *   - On a successful upload the sheet stays open with zone/tags
 *     prefilled, ready for the next photo.
 */

export interface PhotoUploadDefaults {
  domainId?: string | null;
  zoneId?: string | null;
  entityType?: AssetEntityType | null;
  entityId?: string | null;
  tags?: string[];
}

export default function PhotoUploadSheet({
  open,
  onOpenChange,
  defaults,
}: {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  defaults?: PhotoUploadDefaults;
}) {
  const upload = useUploadAsset();
  const domainsQ = useDomains();
  const zonesQ = useZones();
  const fileRef = useRef<HTMLInputElement>(null);

  const [domainId, setDomainId] = useState<string>(defaults?.domainId ?? '');
  const [zoneId, setZoneId] = useState<string>(defaults?.zoneId ?? '');
  const [caption, setCaption] = useState('');
  const [tags, setTags] = useState<string[]>(defaults?.tags ?? []);
  const [tagInput, setTagInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [recent, setRecent] = useState<string | null>(null);
  // Local preview of the picked file so the dropzone visibly confirms
  // the selection — without this, the user can't tell whether their
  // tap on "capture" landed and feels like upload silently failed.
  const [picked, setPicked] = useState<{ file: File; url: string | null } | null>(null);

  useEffect(() => {
    if (open) {
      // Re-init from defaults when re-opening — but preserve any
      // tags the operator was building up in the prior session.
      setDomainId(defaults?.domainId ?? '');
      setZoneId(defaults?.zoneId ?? '');
      setCaption('');
      setError(null);
      setRecent(null);
      clearPicked();
      if (defaults?.tags && defaults.tags.length > 0) setTags(defaults.tags);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Revoke any outstanding object URL on unmount.
  useEffect(() => {
    return () => {
      if (picked?.url) URL.revokeObjectURL(picked.url);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function clearPicked() {
    setPicked((prev) => {
      if (prev?.url) URL.revokeObjectURL(prev.url);
      return null;
    });
    if (fileRef.current) fileRef.current.value = '';
  }

  function onFileChange(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0] ?? null;
    setError(null);
    setRecent(null);
    setPicked((prev) => {
      if (prev?.url) URL.revokeObjectURL(prev.url);
      if (!file) return null;
      const url = file.type.startsWith('image/') ? URL.createObjectURL(file) : null;
      return { file, url };
    });
  }

  function commitTagInput() {
    const next = tagInput
      .split(',')
      .map((t) => t.trim().toLowerCase())
      .filter((t) => t.length > 0 && !tags.includes(t));
    if (next.length > 0) setTags((prev) => [...prev, ...next]);
    setTagInput('');
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    const file = fileRef.current?.files?.[0];
    if (!file) {
      setError('Pick a photo first.');
      return;
    }
    try {
      const created = await upload.mutateAsync({
        file,
        meta: {
          domainId: domainId || null,
          zoneId: zoneId || null,
          entityType: defaults?.entityType ?? null,
          entityId: defaults?.entityId ?? null,
          caption: caption.trim() || null,
          tags,
        },
      });
      setRecent(created.id);
      // Clear file input + preview + caption so next shot is one tap
      // away; keep zone + tags so a survey run stays in capture flow.
      clearPicked();
      setCaption('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed.');
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[92vh] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Add photo</SheetTitle>
          <SheetDescription>
            Open the camera or pick from the library. Up to 8 MB. JPEG, PNG, WebP, or PDF.
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={onSubmit} className="space-y-4 pt-4 pb-2" data-testid="photo-form">
          <div className="relative">
            <label
              htmlFor="photo-file"
              className={
                picked
                  ? 'block cursor-pointer overflow-hidden rounded-lg border border-rule bg-card transition-colors hover:border-primary/40'
                  : 'flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-rule px-3 py-10 transition-colors hover:border-primary/40 hover:bg-accent/30'
              }
            >
              {picked ? (
                picked.url ? (
                  <span className="block" data-testid="photo-preview">
                    <img
                      src={picked.url}
                      alt="Selected photo preview"
                      className="block max-h-72 w-full object-contain bg-muted"
                    />
                    <span className="flex items-center justify-between gap-2 px-3 py-2 text-xs text-muted-foreground">
                      <span className="truncate font-mono">{picked.file.name}</span>
                      <span className="font-mono tabular-nums">
                        {formatBytes(picked.file.size)}
                      </span>
                    </span>
                  </span>
                ) : (
                  <span
                    className="flex items-center gap-3 px-4 py-5"
                    data-testid="photo-preview"
                  >
                    <FileText className="h-8 w-8 shrink-0 text-muted-foreground" aria-hidden />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-mono text-sm">
                        {picked.file.name}
                      </span>
                      <span className="block font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                        {formatBytes(picked.file.size)} · ready to upload
                      </span>
                    </span>
                  </span>
                )
              ) : (
                <>
                  <Camera className="h-8 w-8 text-muted-foreground" aria-hidden />
                  <span className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                    Tap to capture or upload
                  </span>
                </>
              )}
              <input
                ref={fileRef}
                id="photo-file"
                type="file"
                name="file"
                accept="image/*,application/pdf"
                capture="environment"
                required
                className="sr-only"
                data-testid="photo-file"
                onChange={onFileChange}
              />
            </label>
            {picked ? (
              <button
                type="button"
                onClick={clearPicked}
                aria-label="Remove selected file"
                className="absolute right-2 top-2 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-background/90 text-muted-foreground shadow-sm ring-1 ring-rule backdrop-blur transition-colors hover:text-foreground"
                data-testid="photo-clear"
              >
                <X className="h-4 w-4" aria-hidden />
              </button>
            ) : null}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="photo-domain" className="font-mono text-[11px] uppercase tracking-wider">
                Domain
              </Label>
              <select
                id="photo-domain"
                value={domainId}
                onChange={(e) => setDomainId(e.target.value)}
                className="h-11 w-full rounded-md border border-input bg-background px-3 text-base focus:outline-none focus:ring-2 focus:ring-ring"
                data-testid="photo-domain"
              >
                <option value="">No domain</option>
                {(domainsQ.data ?? []).map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="photo-zone" className="font-mono text-[11px] uppercase tracking-wider">
                Zone
              </Label>
              <select
                id="photo-zone"
                value={zoneId}
                onChange={(e) => setZoneId(e.target.value)}
                className="h-11 w-full rounded-md border border-input bg-background px-3 text-base focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="">No zone</option>
                {(zonesQ.data ?? []).map((z) => (
                  <option key={z.id} value={z.id}>
                    {z.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="photo-caption" className="font-mono text-[11px] uppercase tracking-wider">
              Caption
            </Label>
            <Input
              id="photo-caption"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="Optional — what is this?"
              className="h-11 text-base"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="photo-tags" className="font-mono text-[11px] uppercase tracking-wider">
              Tags
            </Label>
            <Input
              id="photo-tags"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onBlur={commitTagInput}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ',') {
                  e.preventDefault();
                  commitTagInput();
                }
              }}
              placeholder="Type a tag, then enter (or comma)"
              className="h-11 text-base"
            />
            {tags.length > 0 ? (
              <div className="flex flex-wrap gap-1.5 pt-1">
                {tags.map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setTags((prev) => prev.filter((x) => x !== t))}
                    className="inline-flex items-center gap-1 rounded-full border border-rule bg-card px-2 py-0.5 text-[11px] font-mono uppercase tracking-wider text-muted-foreground hover:text-foreground hover:border-primary/30"
                  >
                    {t} <span aria-hidden>×</span>
                  </button>
                ))}
              </div>
            ) : null}
          </div>

          {error ? (
            <p className="text-sm text-warning font-medium" role="alert">
              {error}
            </p>
          ) : null}
          {recent ? (
            <p className="text-sm text-primary font-medium" role="status">
              Saved. Pick another photo to keep going, or close.
            </p>
          ) : null}

          <div className="sticky bottom-0 -mx-5 px-5 pt-3 pb-1 bg-card border-t border-rule flex gap-2 justify-end">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Done
            </Button>
            <Button type="submit" disabled={upload.isPending} data-testid="photo-submit">
              {upload.isPending ? 'Uploading…' : 'Save photo'}
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
